import { inject, Injectable } from '@angular/core';
import { IsActiveMatchOptions, Router } from '@angular/router';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { SonaAnalytics } from '@sona/angular-util-analytics';
import { Features, OrganizationId, SubscriptionPlanName, SubscriptionStatus } from '@sona/shared-model-common';
import { Session } from '@supabase/supabase-js';
import { jwtDecode, JwtPayload } from 'jwt-decode';

import { SUPABASE } from '../tokens/supabase.token';

import { SetSession } from './auth.actions';

export interface AuthStateModel {
    session: Session | null;
}

interface SonaJwtPayload extends JwtPayload {
    features: Features;
    organization_id: OrganizationId;
    organization_name: string;
    subscription_plan: SubscriptionPlanName;
    subscription_status: SubscriptionStatus;
    subscription_credits: number;
    subscription_domains: number;
    subscription_usage: {
        creditsUsed: number;
        periodStart: Date;
        periodEnd: Date;
    };
    featurebase_identity_verification_hash?: string;
    organization_featurebase_identity_verification_hash?: string;
}

const EXACT_PATH_MATCH_OPTIONS: IsActiveMatchOptions = {
    paths: 'exact',
    fragment: 'ignored',
    matrixParams: 'ignored',
    queryParams: 'ignored'
};

@State<AuthStateModel>({
    name: 'auth',
    defaults: {
        session: null
    }
})
@Injectable()
export class AuthState implements NgxsOnInit {
    /**
     * The authenticated session from Supabase
     *
     * @param state
     */
    @Selector()
    static session(state: AuthStateModel) {
        return state?.session;
    }

    /**
     * The access token of the authenticated user
     *
     * @param state
     */
    @Selector()
    static accessToken(state: AuthStateModel) {
        return state?.session?.access_token;
    }

    /**
     * Whether the user is authenticated
     *
     * @param state
     */
    @Selector()
    static isAuthenticated(state: AuthStateModel) {
        return state?.session != null;
    }

    /**
     * The user of the authenticated session
     *
     * @param state
     */
    @Selector()
    static user(state: AuthStateModel) {
        return state?.session?.user;
    }

    @Selector()
    static jwtPayload(state: AuthStateModel) {
        const accessToken = state?.session?.access_token;

        if (!accessToken) {
            return null;
        }

        return jwtDecode<SonaJwtPayload>(accessToken);
    }

    /**
     * The enabled features for the current user
     *
     * @param payload - The JWT payload
     */
    @Selector([AuthState.jwtPayload])
    static features(payload: SonaJwtPayload | null) {
        return payload?.features;
    }

    /**
     * The usage limits for the current user
     *
     * @param payload - The JWT payload
     */
    @Selector([AuthState.jwtPayload])
    static usageLimits(payload: SonaJwtPayload | null) {
        return {
            credits: payload?.subscription_credits,
            domains: payload?.subscription_domains
        };
    }

    /**
     * The usage for the current user
     *
     * @param payload - The JWT payload
     */
    @Selector([AuthState.jwtPayload])
    static usage(payload: SonaJwtPayload | null) {
        return payload?.subscription_usage;
    }

    @Selector([AuthState.jwtPayload])
    static subscriptionPlan(payload: SonaJwtPayload | null) {
        return payload?.subscription_plan;
    }

    @Selector([AuthState.jwtPayload])
    static subscriptionStatus(payload: SonaJwtPayload | null) {
        return payload?.subscription_status;
    }

    @Selector([AuthState.jwtPayload])
    static organizationId(payload: SonaJwtPayload | null) {
        return payload?.organization_id;
    }

    @Selector([AuthState.jwtPayload])
    static organizationName(payload: SonaJwtPayload | null) {
        return payload?.organization_name;
    }

    /**
     * The Featurebase identity verification hash for the current user
     *
     * @param payload - The JWT payload
     */
    @Selector([AuthState.jwtPayload])
    static featurebaseIdentityVerificationHash(payload: SonaJwtPayload | null) {
        return payload?.featurebase_identity_verification_hash;
    }

    /**
     * The Featurebase identity verification hash for the current organization
     *
     * @param payload - The JWT payload
     */
    @Selector([AuthState.jwtPayload])
    static organizationFeaturebaseIdentityVerificationHash(payload: SonaJwtPayload | null) {
        return payload?.organization_featurebase_identity_verification_hash;
    }

    private readonly _supabase = inject(SUPABASE);
    private readonly _router = inject(Router);
    private readonly _analytics = inject(SonaAnalytics);

    ngxsOnInit(ctx: StateContext<AuthStateModel>) {
        this._supabase.auth.getSession().then(({ data: { session } }) => {
            ctx.dispatch(new SetSession(session));
        });

        this._supabase.auth.onAuthStateChange(async (event, session) => {
            ctx.dispatch(new SetSession(session));
        });
    }

    @Action(SetSession)
    async setSession(
        ctx: StateContext<AuthStateModel>,
        { session }: SetSession
    ) {
        if (session) {
            this._analytics.setUser({
                userId: session.user?.id,
                email: session.user?.email,
                isAnonymous: session.user?.is_anonymous,
                role: session.user?.role
            });
        }

        const hasSession = ctx.getState().session !== null;

        ctx.patchState({ session });

        if (hasSession && session == null) {
            // The home route '/' redirects to '/scan' by default
            if (
                this._router.isActive('/', EXACT_PATH_MATCH_OPTIONS) ||
                this._router.isActive('/scan', EXACT_PATH_MATCH_OPTIONS)
            ) {
                window.location.reload();
            } else {
                void this._router.navigateByUrl('/');
            }
        }
    }
}
